import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './chat-bubble';
import type { ChatBubble } from '../state/chat-store';

@customElement('bia-chat-messages')
export class BiaChatMessages extends LitElement {
  static styles = css`
    :host {
      display: block;
      flex: 1 1 0;
      min-width: 0;
      min-height: 0;
      width: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 14px 18px 4px;
      background: var(--bia-background, #fff);
      scrollbar-width: thin;
      scrollbar-color: var(--bia-border, #e8e5dc) transparent;
      box-sizing: border-box;
    }
    :host::-webkit-scrollbar {
      width: 6px;
    }
    :host::-webkit-scrollbar-thumb {
      background: var(--bia-border, #e8e5dc);
      border-radius: 999px;
    }
    .list {
      display: flex;
      flex-direction: column;
      min-width: 0;
      max-width: 100%;
    }
    .empty {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
      padding: 12px 2px 0;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
      animation: empty-in 320ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }
    .empty h2,
    .empty p {
      margin: 0;
      min-width: 0;
      max-width: 100%;
      overflow-wrap: anywhere;
      word-wrap: break-word;
    }
    .empty .greet {
      font-family: var(--bia-font-display, Georgia, serif);
      font-style: italic;
      font-weight: 500;
      font-size: 22px;
      line-height: 1.15;
      letter-spacing: -0.02em;
      color: var(--bia-text, #0f0f0e);
    }
    .empty .pitch {
      font-family: var(--bia-font-family, sans-serif);
      font-size: 13px;
      line-height: 1.55;
      color: var(--bia-text-muted, #6b6b66);
    }
    .empty .rule {
      width: 28px;
      height: 1px;
      background: var(--bia-text, #0f0f0e);
      margin: 2px 0 4px;
      opacity: 0.35;
      justify-self: start;
    }
    .empty.warn .greet {
      font-size: 18px;
    }
    @keyframes empty-in {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .loading {
      align-self: flex-start;
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 6px;
      padding: 4px 2px;
      font-family: var(--bia-font-display, Georgia, serif);
      font-style: italic;
      font-size: 13px;
      color: var(--bia-text-muted, #6b6b66);
    }
    .loading .dots {
      display: inline-flex;
      gap: 3px;
    }
    .loading .dot {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--bia-text, #0f0f0e);
      opacity: 0.3;
      animation: dot 1.2s ease-in-out infinite both;
    }
    .loading .dot:nth-child(2) {
      animation-delay: 0.15s;
    }
    .loading .dot:nth-child(3) {
      animation-delay: 0.3s;
    }
    @keyframes dot {
      0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
      40%           { opacity: 0.9; transform: translateY(-2px); }
    }
  `;

  @property({ type: Array }) messages: readonly ChatBubble[] = [];
  @property({ type: Boolean }) loading = false;
  @property({ type: Boolean }) noReport = false;

  updated(): void {
    // Defer twice: once for Lit child updates to flush, once for layout to settle.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.scrollTop = this.scrollHeight;
      });
    });
  }

  render() {
    if (this.noReport) {
      return html`
        <div class="empty warn">
          <h2 class="greet">Escolha um relatório</h2>
          <div class="rule"></div>
          <p class="pitch">
            Selecione um relatório no topo pra começar a conversar sobre os dados dele.
          </p>
        </div>
      `;
    }
    if (!this.messages.length && !this.loading) {
      return html`
        <div class="empty">
          <h2 class="greet">Olá.</h2>
          <div class="rule"></div>
          <p class="pitch">
            Pergunte sobre seus dados — números, tendências, comparações ou análises.
          </p>
        </div>
      `;
    }
    return html`
      <div class="list">
        ${this.messages.map(
          (m) => html`<bia-chat-bubble
            role=${m.role}
            .content=${m.content}
            .envelopeType=${m.envelopeType ?? 'text'}
            .data=${m.data ?? null}
            .charts=${m.charts ?? null}
          ></bia-chat-bubble>`,
        )}
        ${this.loading
          ? html`
              <div class="loading">
                BIA pensando
                <span class="dots">
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                </span>
              </div>
            `
          : ''}
      </div>
    `;
  }
}
